import { Component, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { MaterialUIModule } from '@app/shared/modules/material-ui/material-ui.module';
import { GridActionsComponent, GridAction, ColumnConfig } from '@app/shared/custom-component/grid-actions/grid-actions.component';
import { AlertService } from '@app/@core/services/alert.service';
import {
  columnsForExcelExport,
  headersToColumnConfigs,
  mergeVisibleColumnDefaultsWithChooserLock,
  withColumnChooserLock
} from '../../lookup-grid-columns.util';

@Component({
  selector: 'lookup-user-legacy-data-tab',
  standalone: true,
  imports: [CommonModule, MaterialUIModule, DataGridComponent, GridActionsComponent],
  templateUrl: './lookup-user-legacy-data-tab.component.html',
  styleUrls: ['./lookup-user-legacy-data-tab.component.scss']
})
export class LookupUserLegacyDataTabComponent {
  showDegreePanel = false;
  showTeachingPanel = false;
  showTestingPanel = false;
  showRemarksPanel = false;
  showDocumentsPanel = false;

  degreeGridConfig: DataGridVO;
  teachingGridConfig: DataGridVO;
  testingGridConfig: DataGridVO;
  remarksGridConfig: DataGridVO;
  documentsGridConfig: DataGridVO;

  readonly degreeGridActions: GridAction[] = [
    { type: 'export', id: 'legacy_degree_export', title: 'Export', tooltip: 'Export' },
    { type: 'columnChooser', id: 'legacy_degree_column_chooser', title: 'Column Chooser', tooltip: 'Column Chooser' }
  ];
  readonly teachingGridActions: GridAction[] = [
    { type: 'export', id: 'legacy_teaching_export', title: 'Export', tooltip: 'Export' },
    { type: 'columnChooser', id: 'legacy_teaching_column_chooser', title: 'Column Chooser', tooltip: 'Column Chooser' }
  ];
  readonly testingGridActions: GridAction[] = [
    { type: 'export', id: 'legacy_testing_export', title: 'Export', tooltip: 'Export' },
    { type: 'columnChooser', id: 'legacy_testing_column_chooser', title: 'Column Chooser', tooltip: 'Column Chooser' }
  ];
  readonly remarksGridActions: GridAction[] = [
    { type: 'export', id: 'legacy_remarks_export', title: 'Export', tooltip: 'Export' },
    { type: 'columnChooser', id: 'legacy_remarks_column_chooser', title: 'Column Chooser', tooltip: 'Column Chooser' }
  ];
  readonly documentsGridActions: GridAction[] = [
    { type: 'export', id: 'legacy_documents_export', title: 'Export', tooltip: 'Export' },
    { type: 'columnChooser', id: 'legacy_documents_column_chooser', title: 'Column Chooser', tooltip: 'Column Chooser' }
  ];

  showDegreeColumnChooser = false;
  degreeAllColumns: ColumnConfig[] = [];
  degreeVisibleColumns: Record<string, boolean> = {};

  showTeachingColumnChooser = false;
  teachingAllColumns: ColumnConfig[] = [];
  teachingVisibleColumns: Record<string, boolean> = {};

  showTestingColumnChooser = false;
  testingAllColumns: ColumnConfig[] = [];
  testingVisibleColumns: Record<string, boolean> = {};

  showRemarksColumnChooser = false;
  remarksAllColumns: ColumnConfig[] = [];
  remarksVisibleColumns: Record<string, boolean> = {};

  showDocumentsColumnChooser = false;
  documentsAllColumns: ColumnConfig[] = [];
  documentsVisibleColumns: Record<string, boolean> = {};

  @ViewChild('degreeGrid') degreeGridRef?: DataGridComponent;
  @ViewChild('teachingGrid') teachingGridRef?: DataGridComponent;
  @ViewChild('testingGrid') testingGridRef?: DataGridComponent;
  @ViewChild('remarksGrid') remarksGridRef?: DataGridComponent;
  @ViewChild('documentsGrid') documentsGridRef?: DataGridComponent;

  private readonly searchHeader = (
    caption: string,
    dataField: string,
    options?: { minWidth?: number; allowFiltering?: boolean }
  ) => ({
    dataField,
    caption,
    allowSearch: { isSearchEnable: true, type: 'string' as const },
    ...(options?.minWidth != null ? { minWidth: options.minWidth } : {}),
    ...(options?.allowFiltering !== undefined ? { allowFiltering: options.allowFiltering } : {})
  });

  private readonly dateSearchColumn = (
    caption: string,
    dataField: string,
    options?: { minWidth?: number }
  ) => ({
    dataField,
    caption,
    minWidth: options?.minWidth ?? 128,
    allowSearch: { isSearchEnable: true, type: 'date' as const },
    allowFiltering: true,
    dataType: 'date' as const,
    filterOperations: ['=', '<', '>'],
    editorOptions: {
      displayFormat: 'MM/dd/yyyy',
      inputAttr: { inputmode: 'numeric', pattern: '[0-9/]*' }
    }
  });

  constructor(private readonly alertService: AlertService) {
    const cw = 120;
    const baseGrid = (headers: any[], showActions: boolean) =>
      DataGridVO.create({
        customHeaders: headers,
        data: [],
        showActionsColumn: showActions,
        showFiltering: true,
        showSearchRow: true,
        showPaging: true,
        customPageSize: 10,
        customNoDataText: 'No records',
        role: 'registration',
        activeTab: 'default',
        showQueueActions: false,
        _changeKey: Date.now()
      });

    this.degreeGridConfig = baseGrid(
      [
        this.searchHeader('Load Major/Minor Info', 'load_major_minor_info', { minWidth: 180, allowFiltering: true }),
        this.searchHeader('Number', 'number', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Degree Code', 'degree_code', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Institution', 'institution', { minWidth: 160, allowFiltering: true }),
        this.searchHeader('State', 'state', { minWidth: 96, allowFiltering: true }),
        this.dateSearchColumn('Conferred Date', 'conferred_date', { minWidth: 140 })
      ],
      false
    );

    this.teachingGridConfig = baseGrid(
      [
        this.searchHeader('State', 'state', { minWidth: 96, allowFiltering: true }),
        this.searchHeader('County', 'county', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('District', 'district', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Status', 'status', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Employed', 'employed', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Terminated', 'terminated', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Experience', 'experience', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Amount', 'amount', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Job Class', 'job_class', { minWidth: cw, allowFiltering: true })
      ],
      false
    );

    this.testingGridConfig = baseGrid(
      [
        this.searchHeader('Test', 'test', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Description', 'description', { minWidth: 200, allowFiltering: true }),
        this.dateSearchColumn('Test Date', 'test_date', { minWidth: 140 }),
        this.searchHeader('Verified', 'verified', { minWidth: 96, allowFiltering: true }),
        this.searchHeader('Score', 'score', { minWidth: 96, allowFiltering: true }),
        this.searchHeader('Passed', 'passed', { minWidth: 96, allowFiltering: true })
      ],
      false
    );

    this.remarksGridConfig = baseGrid(
      [
        this.searchHeader('Remark Code', 'remark_code', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Remark Name', 'remark_name', { minWidth: 160, allowFiltering: true }),
        this.searchHeader('Kind Code', 'kind_code', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Kind Description', 'kind_description', { minWidth: 180, allowFiltering: true }),
        this.searchHeader('End Code', 'end_code', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('End Description', 'end_description', { minWidth: 180, allowFiltering: true }),
        this.searchHeader('Provision Code', 'provision_code', { minWidth: cw, allowFiltering: true }),
        this.searchHeader('Provision Description', 'provision_description', { minWidth: 200, allowFiltering: true })
      ],
      false
    );

    this.documentsGridConfig = baseGrid(
      [this.searchHeader('Document Name', 'document_name', { minWidth: 220, allowFiltering: true })],
      true
    );

    this.wireLegacyGridColumns();
  }

  private wireLegacyGridColumns(): void {
    const wire = (cfg: DataGridVO) => {
      const all = withColumnChooserLock(headersToColumnConfigs(cfg.customHeaders));
      const vis = mergeVisibleColumnDefaultsWithChooserLock(
        all.map(c => c.key),
        {}
      );
      return {
        config: cfg.updateConfig({ visibleColumns: vis, _changeKey: Date.now() }),
        all,
        vis
      };
    };

    const d = wire(this.degreeGridConfig);
    this.degreeGridConfig = d.config;
    this.degreeAllColumns = d.all;
    this.degreeVisibleColumns = d.vis;

    const t = wire(this.teachingGridConfig);
    this.teachingGridConfig = t.config;
    this.teachingAllColumns = t.all;
    this.teachingVisibleColumns = t.vis;

    const test = wire(this.testingGridConfig);
    this.testingGridConfig = test.config;
    this.testingAllColumns = test.all;
    this.testingVisibleColumns = test.vis;

    const r = wire(this.remarksGridConfig);
    this.remarksGridConfig = r.config;
    this.remarksAllColumns = r.all;
    this.remarksVisibleColumns = r.vis;

    const doc = wire(this.documentsGridConfig);
    this.documentsGridConfig = doc.config;
    this.documentsAllColumns = doc.all;
    this.documentsVisibleColumns = doc.vis;
  }

  toggleDegreePanel(): void {
    this.showDegreePanel = !this.showDegreePanel;
  }

  toggleTeachingPanel(): void {
    this.showTeachingPanel = !this.showTeachingPanel;
  }

  toggleTestingPanel(): void {
    this.showTestingPanel = !this.showTestingPanel;
  }

  toggleRemarksPanel(): void {
    this.showRemarksPanel = !this.showRemarksPanel;
  }

  toggleDocumentsPanel(): void {
    this.showDocumentsPanel = !this.showDocumentsPanel;
  }

  exportLegacyDegree(): void {
    this.exportLegacyGrid(this.degreeGridRef, this.degreeGridConfig, this.degreeVisibleColumns, 'LegacyDegreeInformation.xlsx');
  }

  toggleDegreeColumnChooser(): void {
    this.showDegreeColumnChooser = !this.showDegreeColumnChooser;
  }

  closeDegreeColumnChooser(): void {
    this.showDegreeColumnChooser = false;
  }

  onDegreeColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.degreeVisibleColumns[ev.key] !== false;
    this.degreeVisibleColumns = { ...this.degreeVisibleColumns, [ev.key]: !cur };
    this.degreeGridConfig = this.degreeGridConfig.updateConfig({
      visibleColumns: { ...this.degreeVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportLegacyTeaching(): void {
    this.exportLegacyGrid(
      this.teachingGridRef,
      this.teachingGridConfig,
      this.teachingVisibleColumns,
      'LegacyTeachingExperience.xlsx'
    );
  }

  toggleTeachingColumnChooser(): void {
    this.showTeachingColumnChooser = !this.showTeachingColumnChooser;
  }

  closeTeachingColumnChooser(): void {
    this.showTeachingColumnChooser = false;
  }

  onTeachingColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.teachingVisibleColumns[ev.key] !== false;
    this.teachingVisibleColumns = { ...this.teachingVisibleColumns, [ev.key]: !cur };
    this.teachingGridConfig = this.teachingGridConfig.updateConfig({
      visibleColumns: { ...this.teachingVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportLegacyTesting(): void {
    this.exportLegacyGrid(this.testingGridRef, this.testingGridConfig, this.testingVisibleColumns, 'LegacyTesting.xlsx');
  }

  toggleTestingColumnChooser(): void {
    this.showTestingColumnChooser = !this.showTestingColumnChooser;
  }

  closeTestingColumnChooser(): void {
    this.showTestingColumnChooser = false;
  }

  onTestingColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.testingVisibleColumns[ev.key] !== false;
    this.testingVisibleColumns = { ...this.testingVisibleColumns, [ev.key]: !cur };
    this.testingGridConfig = this.testingGridConfig.updateConfig({
      visibleColumns: { ...this.testingVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportLegacyRemarks(): void {
    this.exportLegacyGrid(this.remarksGridRef, this.remarksGridConfig, this.remarksVisibleColumns, 'LegacyRemarks.xlsx');
  }

  toggleRemarksColumnChooser(): void {
    this.showRemarksColumnChooser = !this.showRemarksColumnChooser;
  }

  closeRemarksColumnChooser(): void {
    this.showRemarksColumnChooser = false;
  }

  onRemarksColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.remarksVisibleColumns[ev.key] !== false;
    this.remarksVisibleColumns = { ...this.remarksVisibleColumns, [ev.key]: !cur };
    this.remarksGridConfig = this.remarksGridConfig.updateConfig({
      visibleColumns: { ...this.remarksVisibleColumns },
      _changeKey: Date.now()
    });
  }

  exportLegacyDocuments(): void {
    this.exportLegacyGrid(
      this.documentsGridRef,
      this.documentsGridConfig,
      this.documentsVisibleColumns,
      'LegacyDocuments.xlsx'
    );
  }

  toggleDocumentsColumnChooser(): void {
    this.showDocumentsColumnChooser = !this.showDocumentsColumnChooser;
  }

  closeDocumentsColumnChooser(): void {
    this.showDocumentsColumnChooser = false;
  }

  onDocumentsColumnToggle(ev: { key: string; col: ColumnConfig }): void {
    if (ev.col.disabled) return;
    const cur = this.documentsVisibleColumns[ev.key] !== false;
    this.documentsVisibleColumns = { ...this.documentsVisibleColumns, [ev.key]: !cur };
    this.documentsGridConfig = this.documentsGridConfig.updateConfig({
      visibleColumns: { ...this.documentsVisibleColumns },
      _changeKey: Date.now()
    });
  }

  private exportLegacyGrid(
    grid: DataGridComponent | undefined,
    config: DataGridVO,
    visibleColumns: Record<string, boolean>,
    fileName: string
  ): void {
    if (!grid) return;
    const cols = columnsForExcelExport(config.customHeaders, visibleColumns);
    if (cols.length === 0) {
      this.alertService.warn('No columns selected for export');
      return;
    }
    grid.exportFilteredDataToExcel(cols, fileName);
  }

  onLegacyDocumentAction(_data: unknown): void {
    console.log('[Lookup Legacy Data] document action stub');
  }
}
